import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { logisticsApi } from "./ApiClient";
import { generatePatches, generateQueryString } from "./Helpers";
import {
  medicalCompetenceSchema,
  orderedCompetences,
  scheduledShiftSchema,
  scheduledShiftsSchema,
  shiftPresetSchema,
  shiftTypeSchema,
} from "@medoma/models/shifts";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import { fetchPatients } from "./Patients";
import { categorySchema } from "@medoma/models/activities";
import {
  calendarDateSchema,
  machineFormat,
  type IDateTime,
} from "@medoma/models/date-and-time";
import type {
  IMedicalCompetence,
  IScheduledShift,
  IShiftType,
} from "@medoma/models/shifts";
import type { ITeam } from "@medoma/models/patients";
import { resolvePatient } from "./Patients";
import {
  classifyShiftItem,
  shiftItemKindSchema,
  type IShiftItem,
  type IShiftWithContents,
} from "@medoma/models/shiftWithContents";
import {
  unenrichedShiftWithContentsSchema,
  type IUnenrichedShiftItem,
} from "@medoma/models/shiftWithContents.unenriched";

export const compareByCenterType = (
  a: IScheduledShift,
  b: IScheduledShift,
): number => {
  if (
    a.type === shiftTypeSchema.enum.Center &&
    b.type !== shiftTypeSchema.enum.Center
  ) {
    return -1;
  }
  if (
    a.type !== shiftTypeSchema.enum.Center &&
    b.type === shiftTypeSchema.enum.Center
  ) {
    return 1;
  }
  return 0;
};

export const compareByExternalType = (
  a: IScheduledShift,
  b: IScheduledShift,
): number => {
  if (
    a.type === shiftTypeSchema.enum.External &&
    b.type !== shiftTypeSchema.enum.External
  ) {
    return 1;
  }
  if (
    a.type !== shiftTypeSchema.enum.External &&
    b.type === shiftTypeSchema.enum.External
  ) {
    return -1;
  }
  return 0;
};

export const compareByTeam = (
  a: IScheduledShift,
  b: IScheduledShift,
): number => {
  if (a.team && !b.team) {
    return -1;
  }
  if (!a.team && b.team) {
    return 1;
  }
  if (a.team && b.team) {
    return Math.sign(a.team.id - b.team.id);
  }
  return 0;
};

const compareByCurrentUser =
  (currentEmployeeId: number | null | undefined) =>
  (a: IScheduledShift, b: IScheduledShift): number => {
    if (!currentEmployeeId) {
      return 0;
    }
    const aIsCurrentUser = a.employee?.id === currentEmployeeId;
    const bIsCurrentUser = b.employee?.id === currentEmployeeId;
    if (aIsCurrentUser && !bIsCurrentUser) {
      return -1;
    }
    if (!aIsCurrentUser && bIsCurrentUser) {
      return 1;
    }
    return 0;
  };

const compareByPatientTeam =
  (patientTeam: ITeam | null | undefined) =>
  (a: IScheduledShift, b: IScheduledShift): number => {
    if (!patientTeam) {
      return 0;
    }
    const aIsPatientTeam = a.team?.id === patientTeam.id;
    const bIsPatientTeam = b.team?.id === patientTeam.id;
    if (aIsPatientTeam && !bIsPatientTeam) {
      return -1;
    }
    if (!aIsPatientTeam && bIsPatientTeam) {
      return 1;
    }
    return 0;
  };

export const compareShiftsForAssignList =
  ({
    currentEmployeeId,
    patientTeam,
  }: {
    currentEmployeeId: number | null | undefined;
    patientTeam: ITeam | null | undefined;
  }) =>
  (a: IScheduledShift, b: IScheduledShift): number =>
    compareByCurrentUser(currentEmployeeId)(a, b) ||
    compareByPatientTeam(patientTeam)(a, b) ||
    compareByTeam(a, b) ||
    compareByStartTime(a, b);

export const compareByCompetence = (
  a: IScheduledShift,
  b: IScheduledShift,
): number =>
  Math.sign(
    orderedCompetences.indexOf(a.competence) -
      orderedCompetences.indexOf(b.competence),
  );

export const compareByStartTime = (
  a: IScheduledShift,
  b: IScheduledShift,
): number => {
  if (a.startDateTime > b.startDateTime) {
    return 1;
  }
  if (a.startDateTime < b.startDateTime) {
    return -1;
  }
  return 0;
};

export const compareShifts = (a: IScheduledShift, b: IScheduledShift): number =>
  compareByCenterType(a, b) ||
  compareByExternalType(a, b) ||
  compareByTeam(a, b) ||
  compareByCompetence(a, b) ||
  compareByStartTime(a, b);

async function fetchScheduledShifts(from?: string, to?: string) {
  const parsedShifts = (
    await logisticsApi
      .get(`/schedule${generateQueryString({ from, to })}`)
      .json(scheduledShiftsSchema)
  ).sort((shift, otherShift) => compareShifts(shift, otherShift));
  return parsedShifts;
}

async function fetchShiftsWithContents({
  from,
  to,
  ...rest
}:
  | {
      from: string;
      to: string;
    }
  | {
      from: string;
      to: string;
      startTime: Date;
      endTime: Date;
    }) {
  const parsedUnenrichedShiftsWithContents = (
    await logisticsApi
      .get(`/schedule/planned${generateQueryString({ from, to })}`)
      .json(z.array(unenrichedShiftWithContentsSchema))
  ).sort(({ shift }, { shift: otherShift }) =>
    compareShifts(shift, otherShift),
  );

  // AdminTask activities sometimes miss patientId
  const patientIds = deduplicatePrimitiveArray(
    parsedUnenrichedShiftsWithContents.flatMap(({ items }) =>
      items.flatMap((item) => {
        const shiftItem = classifyShiftItem(item);
        switch (shiftItem.kind) {
          case shiftItemKindSchema.enum.route:
            return shiftItem.item.visits.map((visit) => visit.patientId);
          case shiftItemKindSchema.enum.group:
            return [shiftItem.item.patientId];
          case shiftItemKindSchema.enum.occurrence:
            return shiftItem.item.patientId ? [shiftItem.item.patientId] : [];
          default:
            throw shiftItem satisfies never;
        }
      }),
    ),
  );

  const patients = await fetchPatients({ patientIds });

  const enrichShiftItem = (item: IUnenrichedShiftItem): IShiftItem => {
    const shiftItem = classifyShiftItem(item);
    switch (shiftItem.kind) {
      case shiftItemKindSchema.enum.route:
        return {
          ...shiftItem.item,
          visits: shiftItem.item.visits.map(({ patientId, ...visit }) => ({
            ...visit,
            patient: resolvePatient({ patientId, patients }),
          })),
        };
      case shiftItemKindSchema.enum.group: {
        const { patientId, ...group } = shiftItem.item;
        const patient = resolvePatient({ patientId, patients });
        return {
          ...group,
          patient,
          occurrences: group.occurrences.map(
            ({ patientId: _droppedPatientId, ...occurrence }) => ({
              ...occurrence,
              patient,
            }),
          ),
        };
      }
      case shiftItemKindSchema.enum.occurrence: {
        const occurrence = shiftItem.item;
        // An admin task is the only category that can come without a patient.
        if (occurrence.category === categorySchema.enum.AdminTask) {
          const { patientId, ...adminTask } = occurrence;
          return {
            ...adminTask,
            patient: patientId ? resolvePatient({ patientId, patients }) : null,
          };
        }
        const { patientId, ...rest } = occurrence;
        return {
          ...rest,
          patient: resolvePatient({ patientId, patients }),
        };
      }
      default:
        throw shiftItem satisfies never;
    }
  };

  const enrichedShifts = parsedUnenrichedShiftsWithContents.map(
    (unenrichedShift) => ({
      ...unenrichedShift,
      items: unenrichedShift.items.map(enrichShiftItem),
    }),
  );

  if (!("startTime" in rest)) {
    return {
      before: [],
      relevant: enrichedShifts,
      after: [],
    };
  }

  const { startTime, endTime } = rest;

  const before = enrichedShifts.filter((parsedShift) => {
    return parsedShift.shift.startDateTime < startTime;
  });

  const relevant = enrichedShifts.filter((parsedShift) => {
    return (
      parsedShift.shift.startDateTime >= startTime &&
      parsedShift.shift.startDateTime < endTime
    );
  });

  const after = enrichedShifts.filter((parsedShift) => {
    return parsedShift.shift.startDateTime >= endTime;
  });

  return {
    before,
    relevant,
    after,
  };
}

export const shiftKeys = {
  all: ["shifts"] as const,
  lists: () => [...shiftKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), filters] as const,
  listWithContents: (filters: Record<string, unknown>) =>
    [...shiftKeys.lists(), "contents", filters] as const,
  detail: (id: string) => [...shiftKeys.all, id, "details"] as const,
};

export const useScheduledShifts = (fromDate?: Date, toDate?: Date) => {
  const from = fromDate ? machineFormat(fromDate, "yyyy-MM-dd") : undefined;
  const to = toDate ? machineFormat(toDate, "yyyy-MM-dd") : undefined;
  return useQuery({
    queryKey: shiftKeys.list({ from, to }),
    queryFn: () => fetchScheduledShifts(from, to),
    staleTime: 30_000,
  });
};

export const shiftsWithContentsQueryOptions = ({
  selectedDate,
  ...rest
}:
  | {
      selectedDate: Date;
    }
  | {
      selectedDate: Date;
      startTime: Date;
      endTime: Date;
    }) => {
  const formattedFrom = machineFormat(selectedDate, "yyyy-MM-dd");

  if ("startTime" in rest) {
    const { startTime, endTime } = rest;
    const formattedTo = machineFormat(endTime, "yyyy-MM-dd");
    return queryOptions({
      queryKey: shiftKeys.listWithContents({
        from: formattedFrom,
        to: formattedTo,
        start: startTime,
        end: endTime,
      }),
      queryFn: () =>
        fetchShiftsWithContents({
          from: formattedFrom,
          to: formattedTo,
          startTime,
          endTime,
        }),
    });
  }

  return queryOptions({
    queryKey: shiftKeys.listWithContents({
      from: formattedFrom,
      to: formattedFrom,
    }),
    queryFn: () =>
      fetchShiftsWithContents({ from: formattedFrom, to: formattedFrom }),
  });
};

export type ShiftsWithContents = {
  before: IShiftWithContents[];
  relevant: IShiftWithContents[];
  after: IShiftWithContents[];
};

export async function assignGroupToShift(
  groupId: string,
  shiftId: number,
  routeId?: string,
) {
  await logisticsApi.post(`/shifts/${shiftId}/assign`, {
    json: { occurrenceGroupId: groupId, routeId },
  });
}

export async function unassignGroupFromShift(groupId: string, shiftId: number) {
  await logisticsApi.post(`/shifts/${shiftId}/unassign`, {
    json: { occurrenceGroupId: groupId },
  });
}

async function fetchEnabledCompetences() {
  const parsedEnabledCompetences = await logisticsApi
    .get("/competences")
    .json(z.array(medicalCompetenceSchema));

  return parsedEnabledCompetences;
}

const competenceKeys = {
  all: ["enabledCompetences"] as const,
};

export const useEnabledCompetences = () => {
  return useQuery({
    queryKey: competenceKeys.all,
    queryFn: fetchEnabledCompetences,
  });
};

const shiftPresetKeys = {
  all: ["shiftPresets"] as const,
};

export const useShiftPresets = () => {
  return useQuery({
    queryKey: shiftPresetKeys.all,
    queryFn: fetchShiftPresets,
  });
};

export const assignEmployeeToExternalShift = async ({
  employeeId,
  shiftId,
}: {
  employeeId: number;
  shiftId: number;
}) => {
  // (1) Assign to shift in underlying scheduling system
  await logisticsApi.post(`/shifts/${shiftId}/assign-employee/${employeeId}`);
  // (2) Assign to shift in scheduling DB to avoid waiting for sync
  // If 1 fails, return "fatal error"
  // If 2 fails, return "informative error, saying that within 10 minutes, we ok"
};

export const NO_EMPLOYEE = 0;
export const removeAssignedEmployeeFromExternalShift = async ({
  shiftId,
}: {
  shiftId: number;
}) => {
  await assignEmployeeToExternalShift({ employeeId: NO_EMPLOYEE, shiftId });
};

export const updateShiftTimes = async ({
  shiftId,
  startTimeLocal,
  endTimeLocal,
}: {
  shiftId: number;
  startTimeLocal: string;
  endTimeLocal: string;
}) => {
  await logisticsApi.patch(`/shifts/${shiftId}`, {
    json: generatePatches({ startTimeLocal, endTimeLocal }),
  });
};

export const updateShiftEmployee = async ({
  shiftId,
  employeeId,
}: {
  shiftId: number;
  employeeId: number | null;
}) => {
  await logisticsApi.patch(`/shifts/${shiftId}`, {
    json: generatePatches({ employeeId }),
  });
};

export const updateShiftTeam = async ({
  shiftId,
  teamId,
}: {
  shiftId: number;
  teamId: number | null;
}) => {
  await logisticsApi.patch(`/shifts/${shiftId}`, {
    json: generatePatches({ teamId }),
  });
};

export const updateShiftType = async ({
  shiftId,
  shiftType,
}: {
  shiftId: number;
  shiftType: IShiftType | null;
}) => {
  await logisticsApi.patch(`/shifts/${shiftId}`, {
    json: generatePatches({ shiftType }),
  });
};

const fetchShift = async ({ shiftId }: { shiftId: number }) => {
  const parsedShift = await logisticsApi
    .get(`/shifts/${shiftId}`)
    .json(scheduledShiftSchema);
  return parsedShift;
};

export const shiftQueryOptions = ({ shiftId }: { shiftId: number }) =>
  queryOptions({
    queryKey: ["shifts", shiftId],
    queryFn: () => fetchShift({ shiftId }),
  });

export type INewShift = {
  startDateTime: IDateTime;
  endDateTime: IDateTime;
  competence: IMedicalCompetence;
  employeeId: number | null;
  shiftType: IShiftType | null;
  teamId: number | null;
};

export async function createShift({
  startDateTime,
  endDateTime,
  competence,
  employeeId,
  shiftType,
  teamId,
}: INewShift) {
  return await logisticsApi.post("/shifts", {
    json: {
      startDateTime,
      endDateTime,
      competence,
      employeeId,
      shiftType,
      teamId,
    },
  });
}

export async function generatePlan(date: Date) {
  return await logisticsApi.post("/plan", {
    json: { date: calendarDateSchema.encode(date) },
  });
}

export async function deleteShift(shiftId: number) {
  return await logisticsApi.delete(`/shifts/${shiftId}`);
}

async function fetchShiftPresets() {
  return await logisticsApi
    .get("/shift-presets")
    .json(z.array(shiftPresetSchema));
}
